import { useCallback, useEffect, useState } from 'react'
import { IkonaKrzyzyk, IkonaPlus } from './Ikony.js'
import './instalacja.css'

/**
 * Zachęta do dodania aplikacji na ekran główny.
 *
 * Android i przeglądarki oparte na Chromium dają zdarzenie instalacji, więc
 * wystarczy przycisk. Safari na iOS takiego zdarzenia nie ma i nigdy nie
 * będzie miało — tam jedyną drogą jest pokazanie użytkownikowi, gdzie kliknąć.
 * Stąd dwa warianty tego samego paska.
 */

const KLUCZ_ODRZUCENIA = 'apka:instalacja-odrzucona'
/** Po odrzuceniu milkniemy na miesiąc — przypominanie co wejście byłoby natrętne. */
const CISZA_MS = 30 * 24 * 60 * 60 * 1000
/** Pasek pokazujemy z opóźnieniem, żeby nie witał użytkownika zaraz po starcie. */
const OPOZNIENIE_MS = 2500

interface ZdarzenieInstalacji extends Event {
  prompt(): Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}

function juzZainstalowana(): boolean {
  if (window.matchMedia('(display-mode: standalone)').matches) return true
  // Safari na iOS nie wspiera `display-mode`, ma własną flagę.
  return (navigator as { standalone?: boolean }).standalone === true
}

function toIos(): boolean {
  const ua = navigator.userAgent
  const iosKlasyczne = /iPad|iPhone|iPod/.test(ua)
  // iPad z iPadOS podaje się za Maca — rozpoznajemy go po dotyku.
  const iPadUdajacyMaca = ua.includes('Macintosh') && navigator.maxTouchPoints > 1
  return iosKlasyczne || iPadUdajacyMaca
}

function toSafari(): boolean {
  const ua = navigator.userAgent
  return /Safari/.test(ua) && !/CriOS|FxiOS|EdgiOS|OPiOS/.test(ua)
}

function uciszona(): boolean {
  const zapis = localStorage.getItem(KLUCZ_ODRZUCENIA)
  if (zapis === null) return false
  const kiedy = Number(zapis)
  if (Number.isNaN(kiedy)) return false
  return Date.now() - kiedy < CISZA_MS
}

export function Instalacja() {
  const [zdarzenie, ustawZdarzenie] = useState<ZdarzenieInstalacji | null>(null)
  const [pokazIos, ustawPokazIos] = useState(false)
  const [znika, ustawZnika] = useState(false)

  useEffect(() => {
    if (juzZainstalowana() || uciszona()) return

    function przechwyc(zdarzenieOkna: Event) {
      // Bez tego przeglądarka pokaże własny pasek, a chcemy własny wygląd.
      zdarzenieOkna.preventDefault()
      ustawZdarzenie(zdarzenieOkna as ZdarzenieInstalacji)
    }

    window.addEventListener('beforeinstallprompt', przechwyc)

    // iOS nie zgłosi nic samo — decydujemy na podstawie przeglądarki.
    let timer: ReturnType<typeof setTimeout> | null = null
    if (toIos() && toSafari()) {
      timer = setTimeout(() => ustawPokazIos(true), OPOZNIENIE_MS)
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', przechwyc)
      if (timer !== null) clearTimeout(timer)
    }
  }, [])

  const zamknij = useCallback(() => {
    localStorage.setItem(KLUCZ_ODRZUCENIA, String(Date.now()))
    ustawZnika(true)
    setTimeout(() => {
      ustawZdarzenie(null)
      ustawPokazIos(false)
    }, 200)
  }, [])

  const zainstaluj = useCallback(async () => {
    if (zdarzenie === null) return
    await zdarzenie.prompt()
    const { outcome } = await zdarzenie.userChoice
    if (outcome === 'dismissed') localStorage.setItem(KLUCZ_ODRZUCENIA, String(Date.now()))
    ustawZdarzenie(null)
  }, [zdarzenie])

  if (zdarzenie === null && !pokazIos) return null

  const klasa = znika ? 'instalacja instalacja--znika' : 'instalacja'

  return (
    <div className={klasa} role="complementary" aria-label="Dodaj do ekranu głównego">
      <span className="instalacja__ikona" aria-hidden="true">
        <IkonaPlus width={16} height={16} />
      </span>

      <span className="instalacja__tekst">
        <strong>Dodaj do ekranu głównego</strong>
        {pokazIos ? (
          <span>
            Naciśnij <span className="instalacja__udostepnij" aria-hidden="true" /> Udostępnij, a
            potem „Do ekranu początkowego”
          </span>
        ) : (
          <span>Otwiera się jak zwykła aplikacja, działa też bez internetu</span>
        )}
      </span>

      {zdarzenie === null ? null : (
        <button type="button" className="instalacja__akcja" onClick={() => void zainstaluj()}>
          Zainstaluj
        </button>
      )}

      <button
        type="button"
        className="instalacja__zamknij"
        onClick={zamknij}
        aria-label="Zamknij propozycję instalacji"
      >
        <IkonaKrzyzyk width={15} height={15} />
      </button>
    </div>
  )
}
